import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { useToast } from "@/components/ui/use-toast";
import ShiftReportForm from "@/modules/shiftReports/components/ShiftReportForm";
import { useShiftReportBreadcrumbs } from "@/modules/shiftReports/hooks/useShiftReportBreadcrumbs";
import { ShiftReportsService } from "@/modules/shiftReports/service";
import { ApiError } from "@/services/api";
import { ReadShiftReport, WriteShiftReport } from "@/types/shiftReport";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { exists } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useShiftReportEntryColumns } from "../hooks/useShiftReportEntryColumns";

export default function ShiftReportEditor() {
  const { t } = useTranslation();
  const params = useParams();

  const shiftId = params.id as string;
  const queryFn = () => ShiftReportsService.get(shiftId);
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ["shiftReport", params.id],
    queryFn,
    retry: false,
    staleTime: 15000,
    refetchOnWindowFocus: false,
  });

  const mutationFn = async (
    values: WriteShiftReport,
  ): Promise<ReadShiftReport> => {
    const response = await ShiftReportsService.patch(shiftId, values);
    return response;
  };

  const mutation = useMutation<ReadShiftReport, ApiError, WriteShiftReport>({
    mutationFn,
    onSuccess: () => {
      toast({
        title: t("shiftReports.success.updateShiftReport"),
      });
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("shiftReports.errors.updateShiftReport"),
        description,
        variant: "destructive",
      });
    },
  });

  const columns = useShiftReportEntryColumns();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const entries = useMemo(() => {
    return query.data?.department_entries ?? [];
  }, [query.data?.department_entries]);

  const table = useReactTable({
    data: entries,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const breadcrumbItems = useShiftReportBreadcrumbs(query.data);

  if (query.isLoading) {
    return (
      <Spinner label={t("shiftReports.loading")} className="w-full h-full " />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <ShiftReportForm
        data={query.data}
        table={table}
        onSubmit={mutation.mutateAsync}
        disabled={mutation.isPending}
      />
    </>
  );
}
